
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Loader2, Palette } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { candidateFoldersAPI } from "@/services/api";

const colorPresets = [
  { value: '#3B82F6', label: 'Blue', bg: 'bg-blue-500' },
  { value: '#10B981', label: 'Green', bg: 'bg-green-500' },
  { value: '#F59E0B', label: 'Orange', bg: 'bg-orange-500' },
  { value: '#EF4444', label: 'Red', bg: 'bg-red-500' },
  { value: '#8B5CF6', label: 'Purple', bg: 'bg-purple-500' },
  { value: '#06B6D4', label: 'Cyan', bg: 'bg-cyan-500' },
  { value: '#84CC16', label: 'Lime', bg: 'bg-lime-500' },
  { value: '#F97316', label: 'Amber', bg: 'bg-amber-500' },
  { value: '#EC4899', label: 'Pink', bg: 'bg-pink-500' },
  { value: '#6B7280', label: 'Gray', bg: 'bg-gray-500' },
];

export default function CreateFolderModal({ isOpen, onClose, onSuccess, editFolder = null }) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [selectedColor, setSelectedColor] = useState('#3B82F6');
  const [showCustomColor, setShowCustomColor] = useState(false);

  const form = useForm({
    defaultValues: {
      name: "",
      description: "",
      color: "#3B82F6"
    }
  });

  useEffect(() => {
    if (isOpen) {
      if (editFolder) {
        form.reset({
          name: editFolder.name || "",
          description: editFolder.description || "",
          color: editFolder.color || "#3B82F6"
        });
        setSelectedColor(editFolder.color || '#3B82F6');
      } else {
        form.reset({
          name: "",
          description: "",
          color: "#3B82F6"
        });
        setSelectedColor('#3B82F6');
      }
      setShowCustomColor(false);
    }
  }, [isOpen, editFolder, form]);

  const onSubmit = async (data) => {
    try {
      setLoading(true);

      const folderData = {
        ...data,
        color: selectedColor
      };

      if (editFolder) {
        await candidateFoldersAPI.update(editFolder._id, folderData);
        toast({
          title: "Success",
          description: "Folder updated successfully",
        });

      } else {
        await candidateFoldersAPI.create(folderData);
        toast({
          title: "Success",
          description: "Folder created successfully",
        });

      }

      onSuccess();
      onClose();

    } catch (error) {
      console.error("Error with folder:", error);
      toast({
        title: "Error",
        description: error.response?.data?.message || `Failed to ${editFolder ? 'update' : 'create'} folder`,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{editFolder ? 'Edit Folder' : 'Create New Folder'}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 pt-4">
            <FormField
              control={form.control}
              name="name"
              rules={{ required: "Folder name is required" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Folder Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter folder name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description (Optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Enter folder description"
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-3">
              <FormLabel>Folder Color</FormLabel>

              {/* Color Presets */}
              <div className="space-y-2">
                <p className="text-sm text-gray-600">Choose from presets:</p>
                <div className="flex flex-wrap gap-2">
                  {colorPresets.map((color) => (
                    <button
                      key={color.value}
                      type="button"
                      onClick={() => {
                        setSelectedColor(color.value);
                        setShowCustomColor(false);
                      }}
                      className={`w-8 h-8 rounded-full ${color.bg} border-2 transition-all hover:scale-110 ${selectedColor === color.value ? 'border-gray-800 scale-110' : 'border-gray-300'
                        }`}
                      title={color.label}
                    />
                  ))}
                </div>
              </div>

              {/* Custom Color Picker */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowCustomColor(!showCustomColor)}
                  >
                    <Palette className="mr-2 h-4 w-4" />
                    Custom Color
                  </Button>
                  {showCustomColor && (
                    <input
                      type="color"
                      value={selectedColor}
                      onChange={(e) => setSelectedColor(e.target.value)}
                      className="w-10 h-8 border border-gray-300 rounded cursor-pointer"
                    />
                  )}
                </div>
              </div>

              {/* Selected Color Preview */}
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                <div
                  className="w-6 h-6 rounded-full border"
                  style={{ backgroundColor: selectedColor }}
                />
                <span className="text-sm font-medium">Selected: {selectedColor}</span>
              </div>
            </div>

            <DialogFooter className="pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {editFolder ? 'Update Folder' : 'Create Folder'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}